import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, XCircle, Image as ImageIcon } from "@phosphor-icons/react";
import { LeavePill, leaveTypeLabel, PunchInfo } from "@/components/hrms/SelfService";
import { monthLabel } from "@/lib/payslip";
import { useAuth } from "@/contexts/AuthContext";

const thisMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7);
const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");

export const LeaveApprovals = () => {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try { setRows((await api.get(`/hrms/leaves${status ? `?status=${status}` : ""}`)).data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const decide = async (r, s) => {
    const note = s === "rejected" ? window.prompt("Reason for rejecting") : "";
    if (s === "rejected" && !note?.trim()) return;
    try { await api.post(`/hrms/leaves/${r.id}/decide`, { status: s, note }); toast.success(`Leave ${s}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        {["pending", "approved", "rejected", ""].map((s) => (
          <Button key={s || "all"} size="sm" variant={status === s ? "default" : "outline"} className="h-8 capitalize" onClick={() => setStatus(s)} data-testid={`leave-filter-${s || "all"}`}>{s || "All"}</Button>
        ))}
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="data-table" data-testid="leave-approvals-table">
          <thead><tr><th>Employee</th><th>Dates</th><th className="num">Days</th><th>Type</th><th>Reason</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No leave requests.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`leave-row-${r.id}`}>
                <td className="font-medium">{r.employee_name}</td>
                <td>{fmtDate(r.from_date)} → {fmtDate(r.to_date)}</td><td className="num font-mono">{r.days}</td>
                <td>{leaveTypeLabel(r.leave_type)}</td><td className="text-[12px]">{r.reason || "—"}</td>
                <td><LeavePill s={r.status} />{r.decided_by && <div className="text-[11px] text-muted-foreground">{r.decided_by}{r.decision_note && ` · ${r.decision_note}`}</div>}</td>
                <td className="text-right whitespace-nowrap">
                  {r.status === "pending" && (
                    <>
                      <Button size="sm" variant="ghost" className="h-7 text-green-700" onClick={() => decide(r, "approved")} data-testid={`leave-approve-${r.id}`}><CheckCircle size={15} className="mr-1" />Approve</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => decide(r, "rejected")} data-testid={`leave-reject-${r.id}`}><XCircle size={15} className="mr-1" />Reject</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Selfie = ({ id, kind }) => {
  const [src, setSrc] = useState(null);
  const show = async () => {
    try { const { data } = await api.get(`/hrms/attendance/${id}/photo/${kind}`); data.photo ? setSrc(data.photo) : toast.info("No selfie for this punch"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (src) return <img src={src} alt="Selfie" className="w-10 h-10 rounded object-cover inline-block ml-1 align-middle" data-testid={`team-att-selfie-img-${kind}-${id}`} />;
  return <button onClick={show} className="ml-1 text-muted-foreground hover:text-primary align-middle" title="View selfie" data-testid={`team-att-selfie-${kind}-${id}`}><ImageIcon size={14} /></button>;
};

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) : "");
const monthStart = () => `${thisMonth()}-01`;
const todayIst = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const BLANK = { employee: "all", from: monthStart(), to: todayIst(), inAfter: "", inBefore: "", method: "all", status: "all", day: "all" };

const matches = (r, f) => {
  const t = hhmm(r.check_in);
  if (f.inAfter && t < f.inAfter) return false;
  if (f.inBefore && t > f.inBefore) return false;
  if (f.method !== "all" && r.in?.method !== f.method && r.out?.method !== f.method) return false;
  if (f.status === "out" && !r.check_out) return false;
  if (f.status === "in" && r.check_out) return false;
  const dt = r.day_type || "full";
  if (f.day === "late" ? !(r.late_minutes > 0) : f.day !== "all" && dt !== f.day) return false;
  return true;
};

const DAY_STYLE = { full: "bg-green-100 text-green-700", half: "bg-amber-100 text-amber-800", absent: "bg-red-100 text-red-700" };
const DAY_LABEL = { full: "Full Day", half: "Half Day", absent: "Absent" };
const lateText = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

export const DayPill = ({ r, testid }) => {
  const dt = r.day_type || "full";
  const approved = r.day_type_by && dt !== r.day_type_auto;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" data-testid={testid}>
      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${DAY_STYLE[dt]}`}>{DAY_LABEL[dt]}</span>
      {r.late_minutes > 0 && <span className="text-[10.5px] text-red-600">Late {lateText(r.late_minutes)}</span>}
      {approved && <span className="text-[10px] text-muted-foreground" title={r.day_type_note || ""}>by {r.day_type_by}</span>}
    </span>
  );
};

const TimingSettings = () => {
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => { try { setT((await api.get("/hrms/attendance-settings")).data); } catch (e) { toast.error(formatApiError(e)); } })(); }, []);
  if (!t) return null;
  const save = async () => {
    setSaving(true);
    try { setT((await api.put("/hrms/attendance-settings", { ...t, grace_minutes: Number(t.grace_minutes) || 0, half_day_upto_hours: Number(t.half_day_upto_hours) || 3 })).data); toast.success("Check-in timing saved"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-flat p-3 flex flex-wrap items-end gap-3" data-testid="att-timing">
      <F label="Office Check-in Time"><Input type="time" value={t.check_in_time} onChange={(e) => setT({ ...t, check_in_time: e.target.value })} className="w-32 h-9 font-mono" data-testid="att-timing-time" /></F>
      <F label="Grace (minutes)"><Input type="number" min="0" value={t.grace_minutes} onChange={(e) => setT({ ...t, grace_minutes: e.target.value })} className="w-28 h-9 font-mono" data-testid="att-timing-grace" /></F>
      <F label="Half Day if late up to (hours)"><Input type="number" min="0.5" step="0.5" value={t.half_day_upto_hours} onChange={(e) => setT({ ...t, half_day_upto_hours: e.target.value })} className="w-28 h-9 font-mono" data-testid="att-timing-half" /></F>
      <F label="Office Check-out Time"><Input type="time" value={t.check_out_time} onChange={(e) => setT({ ...t, check_out_time: e.target.value })} className="w-32 h-9 font-mono" data-testid="att-timing-out" /></F>
      <Button size="sm" className="h-9" onClick={save} disabled={saving} data-testid="att-timing-save">{saving ? "Saving…" : "Save Timing"}</Button>
      <div className="text-[11.5px] text-muted-foreground basis-full">
        On time (within grace) = Full Day · late up to {t.half_day_upto_hours} hrs = Half Day · later = Absent. Admin / HR approve late days as Full Day in "Late Check-in Approvals" below.
      </div>
    </div>
  );
};

const DayActions = ({ r, onChanged }) => {
  const set = async (day_type) => {
    const note = day_type === "full" ? window.prompt("Reason for approving Full Day (optional)") : "";
    if (note === null) return;
    try { onChanged((await api.post(`/hrms/attendance/${r.id}/day-type`, { day_type, note })).data); toast.success(`Marked ${DAY_LABEL[day_type]}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const dt = r.day_type || "full";
  if (dt !== "full") return <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => set("full")} data-testid={`att-approve-full-${r.id}`}>Approve Full Day</Button>;
  if (r.day_type_auto && r.day_type_auto !== "full") return <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => set(r.day_type_auto)} data-testid={`att-revert-${r.id}`}>Undo</Button>;
  return null;
};

const Pick = ({ value, onChange, items, testid, w = "w-40" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${w} h-9`} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const F = ({ label, children }) => <div className="space-y-1"><div className="eyebrow">{label}</div>{children}</div>;

const AttFilters = ({ f, setF, emps }) => {
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  return (
    <div className="card-flat p-3 flex flex-wrap items-end gap-3" data-testid="team-att-filters">
      <F label="Employee"><Pick value={f.employee} onChange={set("employee")} w="w-52" testid="team-att-f-employee"
        items={[["all", "All employees"], ...emps.map((e) => [e.id, `${e.name}${e.employee_code ? ` (${e.employee_code})` : ""}`])]} /></F>
      <F label="From Date"><Input type="date" value={f.from} onChange={set("from")} className="w-40 h-9 font-mono" data-testid="team-att-f-from" /></F>
      <F label="To Date"><Input type="date" value={f.to} onChange={set("to")} className="w-40 h-9 font-mono" data-testid="team-att-f-to" /></F>
      <F label="Check-in After"><Input type="time" value={f.inAfter} onChange={set("inAfter")} className="w-32 h-9 font-mono" data-testid="team-att-f-in-after" /></F>
      <F label="Check-in Before"><Input type="time" value={f.inBefore} onChange={set("inBefore")} className="w-32 h-9 font-mono" data-testid="team-att-f-in-before" /></F>
      <F label="Method"><Pick value={f.method} onChange={set("method")} w="w-32" testid="team-att-f-method" items={[["all", "All"], ["face", "Face"], ["manual", "Manual"]]} /></F>
      <F label="Status"><Pick value={f.status} onChange={set("status")} w="w-40" testid="team-att-f-status" items={[["all", "All"], ["out", "Checked out"], ["in", "Not checked out"]]} /></F>
      <F label="Day"><Pick value={f.day} onChange={set("day")} w="w-36" testid="team-att-f-day" items={[["all", "All"], ["full", "Full Day"], ["half", "Half Day"], ["absent", "Absent"], ["late", "Late (any)"]]} /></F>
      <Button variant="ghost" size="sm" className="h-9" onClick={() => setF(BLANK)} data-testid="team-att-f-reset">Reset</Button>
    </div>
  );
};

const LatePending = ({ rows, onChanged }) => {
  const pending = rows.filter((r) => r.day_type && r.day_type !== "full" && !r.day_type_by);
  return (
    <div className="card-flat p-3 border-amber-300 bg-amber-50/60 space-y-2" data-testid="late-approvals">
      <div className="text-[13px] font-semibold text-amber-900">Late Check-in Approvals · {pending.length} pending</div>
      {pending.length === 0 && <div className="text-[12px] text-muted-foreground" data-testid="late-approvals-empty">No late check-ins waiting for approval in the selected dates.</div>}
      {pending.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-3 flex-wrap text-[12.5px] bg-white rounded border border-border px-3 py-2" data-testid={`late-pending-${r.id}`}>
          <span><b>{r.employee_name}</b> · {fmtDate(r.date)} · in at {tm(r.check_in)} (office {r.shift_start || "—"})</span>
          <span className="flex items-center gap-2"><DayPill r={r} /><DayActions r={r} onChanged={onChanged} /></span>
        </div>
      ))}
    </div>
  );
};

export const TeamAttendance = () => {
  const [f, setF] = useState(BLANK);
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  useEffect(() => {
    (async () => { try { setEmps((await api.get("/hrms/attendance/employees")).data); } catch (e) { toast.error(formatApiError(e)); } })();
  }, []);
  useEffect(() => {
    if (!f.from || !f.to) { return; }
    const q = new URLSearchParams({ from_date: f.from, to_date: f.to, ...(f.employee !== "all" ? { employee_id: f.employee } : {}) });
    (async () => { try { setRows((await api.get(`/hrms/attendance?${q}`)).data); } catch (e) { toast.error(formatApiError(e)); } })();
  }, [f.from, f.to, f.employee]);
  const { user } = useAuth();
  const canDay = ["admin", "hr"].includes(user?.role);
  const shown = useMemo(() => rows.filter((r) => matches(r, f)), [rows, f]);
  const hours = shown.reduce((a, r) => a + (Number(r.hours) || 0), 0);
  const days = shown.reduce((a, r) => a + ({ full: 1, half: 0.5, absent: 0 }[r.day_type || "full"]), 0);
  const replace = (n) => setRows((rs) => rs.map((x) => (x.id === n.id ? n : x)));
  return (
    <div className="space-y-3">
      {canDay && <TimingSettings />}
      <AttFilters f={f} setF={setF} emps={emps} />
      {canDay && <LatePending rows={rows} onChanged={replace} />}
      <div className="text-[12px] text-muted-foreground" data-testid="team-att-count">
        {shown.length} record(s) · {new Set(shown.map((r) => r.employee_id)).size} employee(s) · {days} present day(s) · {hours.toFixed(2)} hrs · {fmtDate(f.from)} to {fmtDate(f.to)}
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="data-table" data-testid="team-att-table">
          <thead><tr><th>Date</th><th>Employee</th><th>Check In</th><th>Check Out</th><th className="num">Hours</th><th>Day</th>{canDay && <th></th>}</tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No attendance matches these filters.</td></tr>}
            {shown.map((r) => (
              <tr key={r.id} data-testid={`team-att-row-${r.id}`}>
                <td>{fmtDate(r.date)}</td><td className="font-medium">{r.employee_name}</td>
                <td>{tm(r.check_in)}<PunchInfo info={r.in} />{r.in?.method === "face" && <Selfie id={r.id} kind="in" />}</td>
                <td>{tm(r.check_out)}<PunchInfo info={r.out} />{r.out?.method === "face" && <Selfie id={r.id} kind="out" />}</td>
                <td className="num font-mono">{r.hours || "—"}</td>
                <td><DayPill r={r} testid={`team-att-day-${r.id}`} /></td>
                {canDay && <td className="text-right"><DayActions r={r} onChanged={replace} /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
